import type { QueryClient, UseMutationOptions } from '@tanstack/react-query'
import {
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type { QueryError } from 'src/queries/network.utils'
import type {
  CardManagementBillingContactResponse,
  CardManagementGeofenceResponse,
  CardManagementLifecycleOperationResponse,
  CardManagementMccRestrictionsResponse,
  CardManagementPaymentChannelsResponse,
  CardManagementStatusChangeResponse,
  CardManagementStatusResponse,
  ChangeCardManagementGeofenceCountryInput,
  ChangeCardManagementGeofenceCountryResponse,
  CreateCardCancellationRequestInput,
  CreateCardFraudReportInput,
  CreateCardManagementStatusChangeInput,
  CreateCardReplacementRequestInput,
  CreatePermanentLimitChangeInput,
  LimitChangeResponse,
  ReplaceCardManagementBillingContactInput,
  ReplaceCardManagementBillingContactResponse,
  ReplaceCardManagementMccRestrictionsInput,
  ReplaceCardManagementMccRestrictionsResponse,
  SetPaymentChannelInput,
  SetPaymentChannelResponse,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  changeCardManagementGeofenceCountry,
  createCardCancellationRequest,
  createCardFraudReport,
  createCardReplacementRequest,
  createPermanentLimitChange,
  fetchAllCardManagementStatementItems,
  fetchCardManagementActivity,
  fetchCardManagementBillingContact,
  fetchCardManagementCapabilities,
  fetchCardManagementCompanyLine,
  fetchCardManagementCurrentCycle,
  fetchCardManagementGeofence,
  fetchCardManagementLimits,
  fetchCardManagementMccRestrictions,
  fetchCardManagementOperation,
  fetchCardManagementPaymentChannels,
  fetchCardManagementPayments,
  fetchCardManagementPaymentSummary,
  fetchCardManagementStatements,
  fetchCardManagementStatus,
  freezePhysicalCard,
  replaceCardManagementBillingContact,
  replaceCardManagementMccRestrictions,
  searchCardManagementGeofenceCountries,
  setCardManagementPaymentChannel,
  unfreezePhysicalCard,
} from 'src/queries/physicalCardManagement/physicalCardManagement.network'
import type { CustomMutationOptions } from 'src/queries/queries.utils'

/**
 * Page input for the cursor-paginated reads. `creditCardId` is nullable here
 * (unlike the network layer's) because the query factory is called during the
 * render that is still resolving the card and gates itself with `enabled`.
 */
type CardManagementPageQueryInput = {
  creditCardId: string | undefined
  /** 1..100. Omit to take the server's documented default. */
  limit?: number
  before?: string
}

export const physicalCardManagementQueryKeys = {
  capabilitiesFamily: () => ['physicalCardManagement', 'capabilities'] as const,
  capabilities: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.capabilitiesFamily(),
      creditCardId,
    ] as const,
  limitsFamily: () => ['physicalCardManagement', 'limits'] as const,
  limits: (creditCardId: string | undefined) =>
    [...physicalCardManagementQueryKeys.limitsFamily(), creditCardId] as const,
  statusFamily: () => ['physicalCardManagement', 'status'] as const,
  status: (creditCardId: string | undefined) =>
    [...physicalCardManagementQueryKeys.statusFamily(), creditCardId] as const,
  mccRestrictionsFamily: () =>
    ['physicalCardManagement', 'mccRestrictions'] as const,
  mccRestrictions: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.mccRestrictionsFamily(),
      creditCardId,
    ] as const,
  billingContactFamily: () =>
    ['physicalCardManagement', 'billingContact'] as const,
  billingContact: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.billingContactFamily(),
      creditCardId,
    ] as const,
  companyLineFamily: () => ['physicalCardManagement', 'companyLine'] as const,
  companyLine: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.companyLineFamily(),
      creditCardId,
    ] as const,
  currentCycleFamily: () => ['physicalCardManagement', 'currentCycle'] as const,
  currentCycle: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.currentCycleFamily(),
      creditCardId,
    ] as const,
  paymentChannelsFamily: () =>
    ['physicalCardManagement', 'paymentChannels'] as const,
  paymentChannels: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.paymentChannelsFamily(),
      creditCardId,
    ] as const,
  geofenceFamily: () => ['physicalCardManagement', 'geofence'] as const,
  geofence: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.geofenceFamily(),
      creditCardId,
    ] as const,
  /**
   * Its own root, NOT a child of `geofence`. The catalogue is reference data
   * scoped to the card; a country change must refresh the snapshot without
   * dropping every cached search page, and a search must not invalidate the
   * snapshot.
   */
  geofenceCountriesFamily: () =>
    ['physicalCardManagement', 'geofenceCountries'] as const,
  geofenceCountries: (creditCardId: string | undefined, query: string) =>
    [
      ...physicalCardManagementQueryKeys.geofenceCountriesFamily(),
      creditCardId,
      query,
    ] as const,
  operationsFamily: () => ['physicalCardManagement', 'operations'] as const,
  operation: (operationId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.operationsFamily(),
      operationId,
    ] as const,
  /**
   * Cursor-paginated read families (Activity, Statements, Payments).
   *
   * Each family is keyed per card, plus every input that changes the response:
   * the `before` cursor, so a continuation page caches beside its predecessor
   * rather than overwriting it, and `limit`, because two callers asking for
   * different page sizes must not share one entry — the smaller answer would
   * be served as if it were the larger. Cursors are opaque: they appear here
   * only as cache-key material and are never parsed.
   */
  activityFamily: () => ['physicalCardManagement', 'activity'] as const,
  /**
   * One entry per card. The accumulated pages live inside a single infinite
   * query, so unlike the paged families below there is no per-cursor segment.
   */
  activity: (creditCardId: string | undefined, limit?: number) =>
    [
      ...physicalCardManagementQueryKeys.activityFamily(),
      creditCardId,
      limit ?? null,
    ] as const,
  statementsFamily: () => ['physicalCardManagement', 'statements'] as const,
  statements: (
    creditCardId: string | undefined,
    limit?: number,
    before?: string,
  ) =>
    [
      ...physicalCardManagementQueryKeys.statementsFamily(),
      creditCardId,
      limit ?? null,
      before ?? null,
    ] as const,
  statementItemsFamily: () =>
    ['physicalCardManagement', 'statementItems'] as const,
  /**
   * One entry per statement, NOT per page: a ledger read walks the cursor to
   * the end and caches the complete result, so there is no per-cursor key here
   * the way there is for the feed-style families above.
   */
  statementItems: (
    creditCardId: string | undefined,
    statementId: string | undefined,
  ) =>
    [
      ...physicalCardManagementQueryKeys.statementItemsFamily(),
      creditCardId,
      statementId,
    ] as const,
  paymentSummaryFamily: () =>
    ['physicalCardManagement', 'paymentSummary'] as const,
  paymentSummary: (creditCardId: string | undefined) =>
    [
      ...physicalCardManagementQueryKeys.paymentSummaryFamily(),
      creditCardId,
    ] as const,
  paymentsFamily: () => ['physicalCardManagement', 'payments'] as const,
  payments: (
    creditCardId: string | undefined,
    limit?: number,
    before?: string,
  ) =>
    [
      ...physicalCardManagementQueryKeys.paymentsFamily(),
      creditCardId,
      limit ?? null,
      before ?? null,
    ] as const,
}

const CAPABILITIES_STALE_TIME_MS = 30_000

/**
 * Retries BEYOND the first attempt, so a transient failure is tried 3 times.
 *
 * React Query passes `failureCount` 0-based on its first invocation, which is
 * what makes `failureCount < 2` two retries rather than one. Stated here
 * because the off-by-one reads the other way at a glance; the attempt count is
 * pinned empirically in `useAccountActivityData.test.tsx` ("surfaces an error,
 * not demo rows, when the read genuinely fails").
 */
const MAX_READ_RETRIES = 2

/**
 * Retry policy for the Activity / Statements / Payments reads.
 *
 * A predicate rather than `retry: false`, matching `transactionActivities`.
 * The distinction is load-bearing now that these tabs separate "the endpoint
 * has nothing for this card" from "the read failed": a `503 CARD-MGMT.15` is
 * transient and worth another attempt before a user sees an error, while
 * `400` (bad cursor/limit), `403` (not enrolled), and `404` (no route or card)
 * are settled answers that retrying can only delay.
 */
const cardManagementReadRetry = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status =
    error && typeof error === 'object' && 'status' in error
      ? error.status
      : undefined

  return (
    status !== 400 &&
    status !== 403 &&
    status !== 404 &&
    failureCount < MAX_READ_RETRIES
  )
}

const GEOFENCE_COUNTRIES_STALE_TIME_MS = 5 * 60_000

/**
 * How long a just-written card status outranks a disagreeing read.
 *
 * Sized to the replication lag the status route shows after a freeze/unfreeze,
 * not to any operation's own runtime: a status change that has not settled
 * server-side comes back `PENDING` and converges through the consuming
 * surface's polling instead. If the read still disagrees when this lapses, the
 * server is the one telling the truth and the cache takes its answer.
 */
const STATUS_WRITE_TRUST_WINDOW_MS = 30_000

/**
 * Last accepted status write per card. Module-scoped because it must outlive
 * the mutation's own component (the surface can remount mid-flight) and is
 * read from the query factory, which has no other channel to it.
 */
const recentStatusWrites = new Map<
  string,
  { card: CardManagementStatusResponse; writtenAt: number }
>()

/**
 * Apply a status-change response to the cache as the newest truth.
 *
 * The single write path for the status key, so the cache and the record the
 * read path reconciles against are decided together, from one response, in one
 * synchronous step.
 */
const applyAuthoritativeStatusWrite = (
  queryClient: QueryClient,
  creditCardId: string,
  data: CardManagementStatusChangeResponse,
): void => {
  const statusKey = physicalCardManagementQueryKeys.status(creditCardId)
  const cached =
    queryClient.getQueryData<CardManagementStatusResponse>(statusKey)
  const accepted = !cached || cached.version <= data.card.version

  queryClient.setQueryData<CardManagementStatusResponse>(
    statusKey,
    (current) =>
      current && current.version > data.card.version ? current : data.card,
  )

  // Any status change retires the previous record unconditionally: a card the
  // user has just acted on again must never be reconciled against the status
  // it held before. A `PENDING` change clears without replacing — its `card` is
  // still the pre-write snapshot, and its convergence polling has to be able to
  // see the new status land — and so does a response the version guard above
  // rejected, which never reached the surface.
  recentStatusWrites.delete(creditCardId)
  if (accepted && data.status === 'SUCCEEDED') {
    recentStatusWrites.set(creditCardId, {
      card: data.card,
      writtenAt: Date.now(),
    })
  }
}

/**
 * Reconcile a freshly read status against the last write, on the read path.
 *
 * Deliberately here and not in `structuralSharing`: that callback is applied to
 * every value entering the query, `setQueryData` included, so it cannot tell a
 * lagging read from a mutation result — an unfreeze issued before any read had
 * agreed with the preceding freeze would be discarded as if it were stale. It
 * also runs again per observer, where holding the previous value pins a
 * subscriber to a version the cache has already moved past. The `queryFn` is
 * the one boundary only reads cross.
 *
 * Self-clearing in both exits — a read that agrees, and a window that lapses,
 * each drop the record — so a card can never be held on a remembered write
 * beyond one window.
 */
const reconcileStatusRead = (
  creditCardId: string,
  read: CardManagementStatusResponse,
): CardManagementStatusResponse => {
  const write = recentStatusWrites.get(creditCardId)
  if (!write) return read
  if (
    write.card.status === read.status ||
    Date.now() - write.writtenAt >= STATUS_WRITE_TRUST_WINDOW_MS
  ) {
    recentStatusWrites.delete(creditCardId)
    return read
  }
  return write.card
}

/**
 * Test-only: module state survives between cases in a file, and a remembered
 * write from one case would suppress the next case's first read.
 */
export const resetRecentStatusWritesForTest = (): void =>
  recentStatusWrites.clear()

const requireCreditCardId = (
  creditCardId: string | undefined,
  resource: string,
): string => {
  if (!creditCardId) {
    throw new Error(`Card Management ${resource} require a creditCardId`)
  }

  return creditCardId
}
/**
 * Narrows the statement id the same way, so the `queryFn` never has to cast.
 * `enabled` already gates the request; this is what turns a gate that was
 * bypassed into a loud failure rather than a request to `/statements//items`.
 */
const requireStatementId = (statementId: string | undefined): string => {
  if (!statementId) {
    throw new Error(
      'Card Management statement item reads require a statementId',
    )
  }

  return statementId
}

export const physicalCardManagementQueries = {
  billingContact: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.billingContact(creditCardId),
      queryFn: () =>
        fetchCardManagementBillingContact(
          requireCreditCardId(creditCardId, 'billing contact reads'),
        ),
      enabled: Boolean(creditCardId),
    }),
  companyLine: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.companyLine(creditCardId),
      queryFn: () =>
        fetchCardManagementCompanyLine(
          requireCreditCardId(creditCardId, 'company line reads'),
        ),
      enabled: Boolean(creditCardId),
    }),
  paymentChannels: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.paymentChannels(creditCardId),
      queryFn: () =>
        fetchCardManagementPaymentChannels(
          requireCreditCardId(creditCardId, 'payment channel reads'),
        ),
      enabled: Boolean(creditCardId),
    }),
  capabilities: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.capabilities(creditCardId),
      queryFn: () =>
        fetchCardManagementCapabilities(
          requireCreditCardId(creditCardId, 'capabilities'),
        ),
      enabled: Boolean(creditCardId),
      retry: false,
      staleTime: CAPABILITIES_STALE_TIME_MS,
    }),
  limits: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.limits(creditCardId),
      queryFn: () =>
        fetchCardManagementLimits(requireCreditCardId(creditCardId, 'limits')),
      enabled: Boolean(creditCardId),
    }),
  /**
   * The status resource is eventually consistent: a read issued right after a
   * freeze/unfreeze can still answer with the pre-write snapshot. Left alone,
   * that read clobbers the write `useStatusChange` just put in this key and the
   * surface silently snaps back to the old status, so the read is reconciled
   * against that write inside {@link STATUS_WRITE_TRUST_WINDOW_MS}.
   *
   * The window is deliberately narrow and self-clearing — the moment a read
   * agrees, or the window lapses, reads win again. An open-ended rule (keep the
   * higher `version` forever) wedges the surface permanently the first time the
   * server's counter does something we did not predict: every later read is
   * discarded and the card sticks at whatever status the cache happened to
   * hold.
   */
  status: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.status(creditCardId),
      // Not `async`: the id guard must reject a missing id by throwing where
      // the caller stands, exactly as every sibling read factory does, rather
      // than handing back a rejected promise.
      queryFn: () => {
        const id = requireCreditCardId(creditCardId, 'status reads')
        return fetchCardManagementStatus(id).then((read) =>
          reconcileStatusRead(id, read),
        )
      },
      enabled: Boolean(creditCardId),
    }),
  mccRestrictions: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.mccRestrictions(creditCardId),
      queryFn: () =>
        fetchCardManagementMccRestrictions(
          requireCreditCardId(creditCardId, 'MCC restriction reads'),
        ),
      enabled: Boolean(creditCardId),
    }),
  geofence: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.geofence(creditCardId),
      queryFn: () =>
        fetchCardManagementGeofence(
          requireCreditCardId(creditCardId, 'geofence reads'),
        ),
      enabled: Boolean(creditCardId),
      // Same policy as the other Card Management reads: retry a transient 503,
      // but treat 403 (not enrolled) / 404 (no route) as settled answers. The
      // page maps those two to a quiet "not available" rather than a retryable
      // error, so retrying them would only delay that.
      retry: cardManagementReadRetry,
    }),
  /**
   * Card-scoped country catalogue.
   *
   * The key carries the TRIMMED query because that is what goes on the wire —
   * keying on the raw input would cache `"gb"` and `"gb "` as separate pages of
   * identical results.
   */
  geofenceCountries: (creditCardId: string | undefined, query: string) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.geofenceCountries(
        creditCardId,
        query.trim(),
      ),
      queryFn: ({ signal }) =>
        searchCardManagementGeofenceCountries(
          {
            creditCardId: requireCreditCardId(
              creditCardId,
              'geofence country searches',
            ),
            query,
          },
          signal,
        ),
      enabled: Boolean(creditCardId),
      retry: cardManagementReadRetry,
      // Reference data for this card. Keeps the picker from refetching when the
      // drawer is reopened or the user backspaces to a prior query.
      staleTime: GEOFENCE_COUNTRIES_STALE_TIME_MS,
    }),
  operation: (operationId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.operation(operationId),
      queryFn: () => {
        if (!operationId) {
          throw new Error(
            'Card Management operation reads require an operationId',
          )
        }
        return fetchCardManagementOperation(operationId)
      },
      enabled: Boolean(operationId),
      retry: false,
      refetchInterval: (query) => {
        const status = query.state.data?.status
        if (status === 'PENDING_APPROVAL') return 30_000
        return status === 'SUCCEEDED' ||
          status === 'FAILED' ||
          status === 'CANCELED'
          ? false
          : 3_000
      },
    }),
  /*
   * Activity / Statements / Payments reads.
   *
   * All share `cardManagementReadRetry`, which does not retry the settled 4xx
   * answers. That matters here because every one of these routes is answered
   * today by a NON-PRODUCTION mock controller — Activity's is
   * `@Profile("!prod")`, and Statements' and Payments' are `dev`/`stage` only —
   * so outside those profiles the route genuinely does not exist and a retry
   * ladder would only delay the demo fallback the user is going to see anyway.
   */
  /**
   * The card's activity feed — cursor-paginated, newest first.
   *
   * `infiniteQueryOptions` rather than a page-per-key `queryOptions`, matching
   * `transactionActivities` (the app's other `nextCursor` feed): React Query
   * owns the accumulated pages, the in-flight guards, and the stale-response
   * handling, so the consuming hook does not reimplement them.
   *
   * `before` is absent from the first request and opaque thereafter — passed
   * straight back from the previous page's `nextCursor`, never parsed.
   */
  activity: ({ creditCardId, limit }: CardManagementPageQueryInput) =>
    infiniteQueryOptions({
      queryKey: physicalCardManagementQueryKeys.activity(creditCardId, limit),
      queryFn: ({ pageParam }) =>
        fetchCardManagementActivity({
          creditCardId: requireCreditCardId(creditCardId, 'activity reads'),
          limit,
          ...(pageParam === undefined ? {} : { before: pageParam }),
        }),
      enabled: Boolean(creditCardId),
      initialPageParam: undefined as string | undefined,
      // `null` is the contract's terminal signal; React Query wants `undefined`.
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      retry: cardManagementReadRetry,
    }),
  statements: ({ creditCardId, limit, before }: CardManagementPageQueryInput) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.statements(
        creditCardId,
        limit,
        before,
      ),
      queryFn: () =>
        fetchCardManagementStatements({
          creditCardId: requireCreditCardId(creditCardId, 'statement reads'),
          limit,
          before,
        }),
      enabled: Boolean(creditCardId),
      retry: cardManagementReadRetry,
    }),
  /**
   * The COMPLETE ledger for one statement — the query walks the cursor rather
   * than exposing pages, because the detail modal totals these rows and a
   * partial ledger renders a total that contradicts the statement above it.
   */
  statementItems: ({
    creditCardId,
    statementId,
  }: {
    creditCardId: string | undefined
    statementId: string | undefined
  }) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.statementItems(
        creditCardId,
        statementId,
      ),
      queryFn: () =>
        fetchAllCardManagementStatementItems({
          creditCardId: requireCreditCardId(
            creditCardId,
            'statement item reads',
          ),
          statementId: requireStatementId(statementId),
        }),
      // Line items are fetched lazily, only once a statement's detail modal is
      // open, so the statement id gates the request alongside the card id.
      enabled: Boolean(creditCardId) && Boolean(statementId),
      retry: cardManagementReadRetry,
    }),
  paymentSummary: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.paymentSummary(creditCardId),
      queryFn: () =>
        fetchCardManagementPaymentSummary(
          requireCreditCardId(creditCardId, 'payment summary reads'),
        ),
      enabled: Boolean(creditCardId),
      retry: cardManagementReadRetry,
    }),
  currentCycle: (creditCardId: string | undefined) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.currentCycle(creditCardId),
      queryFn: () =>
        fetchCardManagementCurrentCycle(
          requireCreditCardId(creditCardId, 'billing cycle reads'),
        ),
      enabled: Boolean(creditCardId),
      retry: cardManagementReadRetry,
    }),
  payments: ({ creditCardId, limit, before }: CardManagementPageQueryInput) =>
    queryOptions({
      queryKey: physicalCardManagementQueryKeys.payments(
        creditCardId,
        limit,
        before,
      ),
      queryFn: () =>
        fetchCardManagementPayments({
          creditCardId: requireCreditCardId(creditCardId, 'payment reads'),
          limit,
          before,
        }),
      enabled: Boolean(creditCardId),
      retry: cardManagementReadRetry,
    }),
}

export const physicalCardManagementMutations = {
  setPaymentChannel: () =>
    mutationOptions<
      SetPaymentChannelResponse,
      QueryError,
      SetPaymentChannelInput
    >({ mutationFn: setCardManagementPaymentChannel }),
  replaceBillingContact: () =>
    mutationOptions<
      ReplaceCardManagementBillingContactResponse,
      QueryError,
      ReplaceCardManagementBillingContactInput
    >({
      mutationFn: replaceCardManagementBillingContact,
    }),
  createCardReplacementRequest: () =>
    mutationOptions<
      CardManagementLifecycleOperationResponse,
      QueryError,
      CreateCardReplacementRequestInput
    >({
      mutationFn: createCardReplacementRequest,
    }),
  createCardFraudReport: () =>
    mutationOptions<
      CardManagementLifecycleOperationResponse,
      QueryError,
      CreateCardFraudReportInput
    >({
      mutationFn: createCardFraudReport,
    }),
  createCardCancellationRequest: () =>
    mutationOptions<
      CardManagementLifecycleOperationResponse,
      QueryError,
      CreateCardCancellationRequestInput
    >({
      mutationFn: createCardCancellationRequest,
    }),
  createPermanentLimitChange: () =>
    mutationOptions<
      LimitChangeResponse,
      QueryError,
      CreatePermanentLimitChangeInput
    >({
      mutationFn: createPermanentLimitChange,
    }),
  freezePhysicalCard: () =>
    mutationOptions<
      CardManagementStatusChangeResponse,
      QueryError,
      CreateCardManagementStatusChangeInput
    >({
      mutationFn: freezePhysicalCard,
    }),
  unfreezePhysicalCard: () =>
    mutationOptions<
      CardManagementStatusChangeResponse,
      QueryError,
      CreateCardManagementStatusChangeInput
    >({
      mutationFn: unfreezePhysicalCard,
    }),
  changeGeofenceCountry: () =>
    mutationOptions<
      ChangeCardManagementGeofenceCountryResponse,
      QueryError,
      ChangeCardManagementGeofenceCountryInput
    >({
      mutationFn: changeCardManagementGeofenceCountry,
    }),
  replaceMccRestrictions: () =>
    mutationOptions<
      ReplaceCardManagementMccRestrictionsResponse,
      QueryError,
      ReplaceCardManagementMccRestrictionsInput
    >({
      mutationFn: replaceCardManagementMccRestrictions,
    }),
}

type StatusChangeMutationOptions = CustomMutationOptions<
  CardManagementStatusChangeResponse,
  QueryError,
  CreateCardManagementStatusChangeInput
>

const useStatusChange = (
  mutationOptionsFactory: () => UseMutationOptions<
    CardManagementStatusChangeResponse,
    QueryError,
    CreateCardManagementStatusChangeInput
  >,
  options?: StatusChangeMutationOptions,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...mutationOptionsFactory(),
    onSuccess: (data, variables, onMutateResult, context) => {
      const statusKey = physicalCardManagementQueryKeys.status(
        variables.creditCardId,
      )
      applyAuthoritativeStatusWrite(queryClient, variables.creditCardId, data)
      if (data.status === 'PENDING') {
        // The contract-only client performs the required immediate refresh.
        // Longer-lived convergence polling awaits EX-40587's runtime timing
        // and lifecycle contract and belongs with the consuming UI slice.
        void queryClient.invalidateQueries({ queryKey: statusKey, exact: true })
      }
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export const useFreezePhysicalCard = (options?: StatusChangeMutationOptions) =>
  useStatusChange(physicalCardManagementMutations.freezePhysicalCard, options)

export const useUnfreezePhysicalCard = (
  options?: StatusChangeMutationOptions,
) =>
  useStatusChange(physicalCardManagementMutations.unfreezePhysicalCard, options)

export const useCreatePermanentLimitChange = (
  options?: CustomMutationOptions<
    LimitChangeResponse,
    QueryError,
    CreatePermanentLimitChangeInput
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...physicalCardManagementMutations.createPermanentLimitChange(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData(
        physicalCardManagementQueryKeys.limits(variables.creditCardId),
        data.limits,
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export const useReplaceCardManagementMccRestrictions = (
  options?: CustomMutationOptions<
    ReplaceCardManagementMccRestrictionsResponse,
    QueryError,
    ReplaceCardManagementMccRestrictionsInput
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...physicalCardManagementMutations.replaceMccRestrictions(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<CardManagementMccRestrictionsResponse>(
        physicalCardManagementQueryKeys.mccRestrictions(variables.creditCardId),
        data.mccRestrictions,
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

/**
 * Billing contact replace (EX-40624 / EX-40631).
 *
 * The PUT returns the authoritative post-write snapshot, so the cache is
 * written from the response rather than invalidated — the next render already
 * holds the new `version`, which is the `expectedVersion` fence for the user's
 * *next* save. An invalidate-only approach would leave the form briefly holding
 * a stale version and fail that save with `CARD-MGMT.28`.
 *
 * Unlike the status family there is no `PENDING` branch to reconcile: the
 * contract fails uncertain outcomes closed instead of accepting them.
 */
export const useReplaceBillingContact = (
  options?: CustomMutationOptions<
    ReplaceCardManagementBillingContactResponse,
    QueryError,
    ReplaceCardManagementBillingContactInput
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...physicalCardManagementMutations.replaceBillingContact(),
    // On the mutation, not on `mutate(...)` — this must still run if the tile
    // unmounts mid-flight, or the cache keeps a stale version.
    onSuccess: (data, variables, onMutateResult, context) => {
      const billingContactKey = physicalCardManagementQueryKeys.billingContact(
        variables.creditCardId,
      )
      queryClient.setQueryData<CardManagementBillingContactResponse>(
        billingContactKey,
        (current) =>
          current && current.version > data.billingContact.version
            ? current
            : data.billingContact,
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

/**
 * Add or remove ONE permitted geofence country (EX-40629).
 *
 * The POST returns the authoritative post-write snapshot — guaranteed
 * AVAILABLE by the contract — so the cache is written from the response rather
 * than invalidated. That matters more here than elsewhere: the drawer supports
 * rapid successive add/remove clicks, and each one fences on the version from
 * the previous response. An invalidate-only approach would hand the next click
 * a stale version and fail it with `CARD-MGMT.22`.
 *
 * A no-op change (adding an already-permitted country) legitimately returns an
 * UNCHANGED version, so the guard below rejects only a strictly OLDER snapshot
 * from an out-of-order completion.
 */
export const useChangeGeofenceCountry = (
  options?: CustomMutationOptions<
    ChangeCardManagementGeofenceCountryResponse,
    QueryError,
    ChangeCardManagementGeofenceCountryInput
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...physicalCardManagementMutations.changeGeofenceCountry(),
    // On the mutation, not on `mutate(...)` — the drawer can close mid-flight
    // and the cache must still take the new version.
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<CardManagementGeofenceResponse>(
        physicalCardManagementQueryKeys.geofence(variables.creditCardId),
        (current) => {
          const currentVersion = current?.version
          const nextVersion = data.geofence.version
          if (
            currentVersion != null &&
            nextVersion != null &&
            currentVersion > nextVersion
          ) {
            return current
          }
          return data.geofence
        },
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

/**
 * Set ONE payment channel.
 *
 * The contract has no multi-channel mutation and each channel carries its own
 * version, so the response patches just that channel back into the collection
 * rather than replacing the whole snapshot — a blanket overwrite would discard
 * sibling channels' versions and make their next write stale.
 */
export const useSetPaymentChannel = (
  options?: CustomMutationOptions<
    SetPaymentChannelResponse,
    QueryError,
    SetPaymentChannelInput
  >,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...physicalCardManagementMutations.setPaymentChannel(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<CardManagementPaymentChannelsResponse>(
        physicalCardManagementQueryKeys.paymentChannels(variables.creditCardId),
        (current) =>
          current === undefined
            ? current
            : {
                ...current,
                channels: current.channels.map((channel) => {
                  if (channel.channel !== data.paymentChannel.channel) {
                    return channel
                  }
                  // Never roll a channel back. An out-of-order completion must
                  // not resurrect a stale version, which would hand the next
                  // write a fence the server already advanced past. `null`
                  // versions belong to UNAVAILABLE channels and have no order.
                  const currentVersion = channel.version
                  const nextVersion = data.paymentChannel.version
                  if (
                    currentVersion !== null &&
                    nextVersion !== null &&
                    currentVersion > nextVersion
                  ) {
                    return channel
                  }
                  return data.paymentChannel
                }),
              },
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
